app.controller("patientVerificationController", [
	"$scope",
	"PatientFormsAPI",
	"$stateParams",
	"$timeout",
	"$interval",
	"countryConfig",
	function ($scope, PatientFormsAPI, $stateParams, $timeout, $interval, countryConfig) {
		var CODE_LENGTH = 4;

		// How long to make the patient wait before asking for another text. Long enough that a slow
		// carrier has a chance to deliver the first one - most of the "it never arrived" presses are
		// really "it has not arrived yet" - and short enough not to strand someone whose text genuinely
		// went missing. The server caps the total number of resends separately.
		var RESEND_COOLDOWN_SECONDS = 30;

		function blankCode() {
			var digits = [];
			for (var i = 0; i < CODE_LENGTH; i++) {
				digits.push("");
			}
			return digits;
		}

		$scope.PatientFormsAPI = PatientFormsAPI;
		$scope.PatientFormsAPI.isProcessing = true;
		$scope.codeDigits = blankCode();
		$scope.codeError = false;
		$scope.dob = { day: "", month: "", year: "" };
		$scope.dobError = false;

		// The two end states the page can settle into, shown inline rather than as a modal. `exhausted`
		// is separate from `error` because it is not the same news: the link was fine, the answers were
		// not, and only the practice can issue another.
		$scope.confirmed = false;
		$scope.error = false;
		$scope.exhausted = false;

		// Which way the link stopped working, when the server said: "expired", "cancelled", "used" or
		// "invalid". Null when it was not said - the page-load check does not give a reason - and the
		// panel then falls back to wording that covers all of them.
		$scope.linkProblem = null;

		// True when a step could not be checked at all - a failure on our side or a dropped connection,
		// not a wrong answer. Kept apart from codeError/dobError so a patient is never told details
		// that may well be right do not match, and the answer they typed is left in place to resend.
		$scope.checkFailed = false;

		// Consent explainers start collapsed so the block stays short on a phone; the patient opens the
		// one they care about. A map rather than two flags so the template shares one toggle.
		$scope.consentHelp = { research: false, ml: false };

		$scope.toggleConsentHelp = function (key) {
			$scope.consentHelp[key] = !$scope.consentHelp[key];
		};

		// The law the consents are given under, named on both consent steps. Comes from countryConfig
		// rather than being written into the template, because this page is served to patients in more
		// than one country and NZ legislation means nothing to an Australian one. countryConfig is
		// resolved from the domain, not from the session, so it is available here even though this page
		// has no login.
		//
		// An empty list hides the block outright, which is what a country whose wording has not been
		// settled should get rather than someone else's law.
		$scope.consentLegislation = countryConfig.consentLegislation || [];

		// Joins the references as prose: "A and B", or "A, B and C". Rendering them as a bare list
		// would read as a menu of things to click rather than as the sentence it is.
		$scope.legislationSeparator = function (index) {
			return index === $scope.consentLegislation.length - 2 ? " and " : ", ";
		};

		// The answers, kept on one object so a bounce back to an earlier step never loses the later
		// ones. Null rather than false for the consents: "not answered yet" has to be distinguishable
		// from "answered no", or a required question would arrive at the server pre-answered.
		$scope.consents = { research: null, ml: null };
		$scope.consentPrompt = { research: false, ml: false };

		// ---------------------------------------------------------------------------------------------
		// The steps.
		//
		// Built from the validated token rather than hard-coded, because a single-channel token has no
		// code to enter. The progress counter reads off this list, so it can never promise a step that
		// will not appear.
		// ---------------------------------------------------------------------------------------------

		$scope.steps = [];
		$scope.stepIndex = 0;

		// How many wrong answers the token has left, as last reported by the server. Null until the
		// patient gets something wrong, or when the server could not tell us - shown as nothing rather
		// than as none left, since telling a patient their link is finished when it is not is worse
		// than saying nothing.
		$scope.attemptsRemaining = null;

		// Below this the count is worth showing; above it, it is noise. The server allows more attempts
		// than this, so a patient who mistypes once early sees nothing - the warning is for someone
		// actually approaching the limit, not for everyone who slips.
		var ATTEMPT_WARNING_THRESHOLD = 3;

		$scope.isAttemptWarningVisible = function () {
			return $scope.attemptsRemaining !== null
				&& $scope.attemptsRemaining > 0
				&& $scope.attemptsRemaining <= ATTEMPT_WARNING_THRESHOLD
				&& ($scope.isStep("code") || $scope.isStep("dob"));
		};

		$scope.currentStep = function () {
			return $scope.steps[$scope.stepIndex];
		};

		$scope.isStep = function (name) {
			return $scope.currentStep() === name;
		};

		// Only the primary button cares, to choose between "Continue" and "Finish and verify". The
		// progress line states the position and leaves the arithmetic to the reader.
		$scope.isLastStep = function () {
			return $scope.stepIndex === $scope.steps.length - 1;
		};

		// Whether this patient has to enter a texted code. A patient with both an email address and a
		// mobile gets the link by email and the code by text, so the code is what proves the mobile. A
		// patient with only one of the two got the link down that channel, and following it is already
		// proof of possession, so there is no code step at all.
		//
		// Defaults to true so that a response without the flag - an older API, or a token issued before
		// single-channel sending existed - still asks for the code rather than silently dropping a
		// required step.
		$scope.mobileCodeRequired = true;

		// What this token actually covers - BOTH, EMAIL or PHONE - so the confirmation names the details
		// that were verified rather than claiming both. Unknown until the token has been validated.
		$scope.channelType = null;

		// Whether to offer to text the code again. The server decides: it turns on the patient still
		// having a mobile number and on the token having resends left, neither of which this page can
		// see. Defaults to false, unlike mobileCodeRequired above - an older API that does not send the
		// flag also has no endpoint to call, so the safe default here is to show no button rather than
		// one that can only fail.
		$scope.resendAvailable = false;

		// null until the patient presses it, then "sending", "sent" or "failed". Separate from
		// PatientFormsAPI.isProcessing on purpose: that flag replaces the whole panel with a spinner,
		// which would rip the code boxes out from under someone who has already typed into them.
		$scope.resendState = null;

		// Seconds left before another resend is allowed. Counts down from RESEND_COOLDOWN_SECONDS.
		$scope.resendCooldown = 0;
		var cooldownTimer = null;

		// True while a step is being checked with the server. Separate from isProcessing for the same
		// reason as resendState: it only disables the button, it does not replace the panel.
		$scope.checking = false;

		// Names the details this token verified, for the confirmation message. Falls back to the pair
		// when the channel is unknown, which is what every token covered before single-channel sending.
		$scope.verifiedDetailsLabel = function () {
			if ($scope.channelType === "EMAIL") return "Your email address has";
			if ($scope.channelType === "PHONE") return "Your mobile number has";
			return "Your email address and mobile number have";
		};

		// The token is validated up front so an expired link says so rather than failing on submit, and
		// so the page knows which steps to build.
		$scope.PatientFormsAPI.verifyToken("PATIENT_COMM_VERIFICATION", $stateParams.token).then(
			function (response) {
				$scope.error = false;
				var data = (response && response.data) || {};
				$scope.mobileCodeRequired = data.mobile_code_required !== false;
				$scope.channelType = data.channel_type || null;
				$scope.resendAvailable = data.mobile_code_resend_available === true;

				if ($scope.mobileCodeRequired) {
					$scope.steps.push("code");
				}
				$scope.steps.push("dob", "research", "ml");
			},
			function () {
				$scope.error = true;
			}
		).then(function () {
			$scope.PatientFormsAPI.isProcessing = false;

			// Put the cursor in the first field so the patient can start typing straight away. iOS
			// ignores programmatic focus outside a gesture, so this never springs the keyboard open.
			if (!$scope.error) {
				focusStep();
			}
		});

		// ---------------------------------------------------------------------------------------------
		// The code boxes.
		// ---------------------------------------------------------------------------------------------

		// Spread a run of digits across the boxes from startIndex on. Typing, pasting and SMS autofill
		// all come through here, so a code that arrives all at once lands the same way as one typed a
		// digit at a time. Returns the index just past the last box filled.
		function applyDigits(startIndex, rawText) {
			var digits = String(rawText || "").replace(/\D/g, "").split("");
			var idx = startIndex;

			while (idx < CODE_LENGTH && digits.length) {
				$scope.codeDigits.splice(idx, 1, digits.shift());
				idx++;
			}
			return idx;
		}

		// Move focus to the box at idx, or drop focus once the code is complete so the mobile keyboard
		// gets out of the way of the button.
		function focusBox(idx) {
			$timeout(function () {
				if (idx < CODE_LENGTH) {
					var box = document.getElementById("mfa-digit-" + idx);
					box && box.focus();
				} else if (document.activeElement && document.activeElement.blur) {
					document.activeElement.blur();
				}
			}, 0);
		}

		// Once a credential step is fully answered there is nothing left for the patient to decide,
		// so pressing Continue is a keystroke that only ever means "yes, the thing I just typed".
		// Submitting for them removes it.
		//
		// The short delay is deliberate: it lets the last digit paint before the panel changes, so the
		// step does not appear to vanish mid-keystroke. It is also the window in which a mistyped last
		// character can be corrected without a wasted attempt being spent.
		//
		// Guarded on `checking` and on the step still being the one we queued for - a queued advance
		// that lands after the patient has already moved on must not fire a second check.
		var AUTO_ADVANCE_DELAY_MS = 350;
		var autoAdvanceTimer = null;

		function cancelAutoAdvance() {
			if (autoAdvanceTimer) {
				$timeout.cancel(autoAdvanceTimer);
				autoAdvanceTimer = null;
			}
		}

		function scheduleAutoAdvance() {
			cancelAutoAdvance();
			if (!$scope.isStepAnswered()) { return; }

			var queuedFor = $scope.currentStep();
			autoAdvanceTimer = $timeout(function () {
				autoAdvanceTimer = null;
				if ($scope.checking || $scope.PatientFormsAPI.isProcessing) { return; }
				if ($scope.currentStep() !== queuedFor) { return; }
				if (!$scope.isStepAnswered()) { return; }
				checkStep(queuedFor);
			}, AUTO_ADVANCE_DELAY_MS);
		}

		$scope.onDigitInput = function (idx) {
			// The boxes accept the whole code, not one character, so autofill can drop it into any one
			// of them. Clear first, then re-apply, so the digits always land left to right from here.
			var typed = $scope.codeDigits[idx];
			$scope.codeDigits.splice(idx, 1, "");
			$scope.codeError = false;
			$scope.checkFailed = false;

			focusBox(applyDigits(idx, typed));
			scheduleAutoAdvance();
		};

		// Select rather than clear on focus: tapping a box to correct one digit used to blank it, and
		// the auto-advance then blanked every digit after it too. Selecting keeps the code intact while
		// still letting the next keystroke overwrite the digit the patient came back for.
		$scope.selectCodeDigit = function (idx) {
			$timeout(function () {
				var box = document.getElementById("mfa-digit-" + idx);
				box && box.select && box.select();
			}, 0);
		};

		$scope.onCodePaste = function ($event) {
			$event.preventDefault();
			var rawEvent = $event.originalEvent || $event;
			var clipboard = rawEvent.clipboardData || window.clipboardData;
			if (!clipboard || !clipboard.getData) return;

			// Always fills from the first box: a pasted code is the whole code, wherever it was dropped.
			// A short paste fills what it can rather than being discarded outright.
			var text = clipboard.getData("text/plain") || clipboard.getData("Text");
			$scope.codeError = false;
			$scope.checkFailed = false;
			focusBox(applyDigits(0, text));
			scheduleAutoAdvance();
		};

		$scope.onDigitKeyDown = function (keyEvent, digitIndex) {
			var BACKSPACE_KEY_CODE = 8;
			var pressed = keyEvent.which || keyEvent.keyCode;
			if (pressed === BACKSPACE_KEY_CODE) {
				var currentValue = $scope.codeDigits[digitIndex] || "";
				if (currentValue === "" && digitIndex > 0) {
					// Backspacing out of an empty box steps back and deletes the digit there, which is
					// what a single row of boxes should feel like to type into.
					keyEvent.preventDefault();
					cancelAutoAdvance();
					$scope.codeDigits.splice(digitIndex - 1, 1, "");
					focusBox(digitIndex - 1);
				}
			}
		};

		// ---------------------------------------------------------------------------------------------
		// The date of birth.
		//
		// Three boxes rather than a native date input: a patient born in the 1940s should not have to
		// scroll a calendar back eighty years on a phone.
		// ---------------------------------------------------------------------------------------------

		var DOB_FIELDS = ["day", "month", "year"];
		var DOB_LENGTHS = { day: 2, month: 2, year: 4 };

		$scope.onDobInput = function (field) {
			$scope.dob[field] = String($scope.dob[field] || "").replace(/\D/g, "");
			$scope.dobError = false;
			$scope.checkFailed = false;

			// Auto-advance only on a full box, so a single-digit day still lets the patient tab on
			// themselves rather than being dragged forward after one keystroke.
			var position = DOB_FIELDS.indexOf(field);
			if ($scope.dob[field].length >= DOB_LENGTHS[field] && position < DOB_FIELDS.length - 1) {
				focusDobField(DOB_FIELDS[position + 1]);
			}

			scheduleAutoAdvance();
		};

		$scope.onDobKeyDown = function (keyEvent, field) {
			var BACKSPACE_KEY_CODE = 8;
			var pressed = keyEvent.which || keyEvent.keyCode;
			var position = DOB_FIELDS.indexOf(field);
			if (pressed === BACKSPACE_KEY_CODE && !$scope.dob[field] && position > 0) {
				cancelAutoAdvance();
				keyEvent.preventDefault();
				focusDobField(DOB_FIELDS[position - 1]);
			}
		};

		function focusDobField(field) {
			$timeout(function () {
				var box = document.getElementById("dob-" + field);
				box && box.focus();
			}, 0);
		}

		// The date as the API wants it, or null when the boxes are not filled in. Padded rather than
		// rejected, so a patient who types 3 for March is understood.
		function dobValue() {
			var day = $scope.dob.day, month = $scope.dob.month, year = $scope.dob.year;
			if (!day || !month || !year || year.length !== 4) return null;
			return year + "-" + ("0" + month).slice(-2) + "-" + ("0" + day).slice(-2);
		}

		// ---------------------------------------------------------------------------------------------
		// The consents.
		// ---------------------------------------------------------------------------------------------

		$scope.setConsent = function (key, value) {
			$scope.consents[key] = value;
			$scope.consentPrompt[key] = false;
		};

		// ---------------------------------------------------------------------------------------------
		// Moving between steps.
		// ---------------------------------------------------------------------------------------------

		$scope.isStepAnswered = function () {
			switch ($scope.currentStep()) {
				case "code":
					return $scope.codeDigits.every(function (c) { return c && c.length === 1; });
				case "dob":
					return !!dobValue();
				case "research":
					return $scope.consents.research !== null;
				case "ml":
					return $scope.consents.ml !== null;
			}
			return false;
		};

		$scope.isSubmitAvailable = function () {
			return !$scope.PatientFormsAPI.isProcessing && !$scope.checking && $scope.isStepAnswered();
		};

		// Back is offered only when the step behind is a consent step. The code and the date of birth
		// have been proven with the server and recorded against the token; going back to them would
		// mean re-proving an answer that is already settled.
		$scope.canGoBack = function () {
			if ($scope.stepIndex === 0) return false;
			var previous = $scope.steps[$scope.stepIndex - 1];
			return previous === "research" || previous === "ml";
		};

		$scope.goBack = function () {
			if (!$scope.canGoBack() || $scope.checking) return;
			$scope.stepIndex--;
			focusStep();
		};

		function focusStep() {
			if ($scope.isStep("code")) {
				focusBox(0);
			} else if ($scope.isStep("dob")) {
				focusDobField("day");
			}
		}

		$scope.next = function () {
			if ($scope.checking || $scope.PatientFormsAPI.isProcessing) return;

			if (!$scope.isStepAnswered()) {
				// The consent steps say why the button is doing nothing; the other two already have a
				// hint under the button.
				if ($scope.isStep("research")) $scope.consentPrompt.research = true;
				if ($scope.isStep("ml")) $scope.consentPrompt.ml = true;
				return;
			}

			// A credential step is proven before the patient is allowed off it, so a wrong code or date
			// of birth is caught where it was typed rather than after the consent questions.
			if ($scope.isStep("code") || $scope.isStep("dob")) {
				checkStep($scope.currentStep());
				return;
			}

			if (!$scope.isLastStep()) {
				$scope.stepIndex++;
				focusStep();
				return;
			}

			submit();
		};

		// Proves one factor with the server. The token records what has been proven, so the completion
		// call at the end has nothing left to check.
		function checkStep(step) {
			$scope.checking = true;
			$scope.checkFailed = false;

			var mobileCode = step === "code" ? $scope.codeDigits.join("") : null;
			var dob = step === "dob" ? dobValue() : null;

			PatientFormsAPI.verifyStep($stateParams.token, mobileCode, dob).then(
				function (response) {
					$scope.checking = false;
					readAttemptsRemaining(response);

					// Advancing is the confirmation that the step passed - the server has recorded it
					// against the token, and there is nothing here for the patient to act on.
					$scope.stepIndex++;
					focusStep();
				},
				function (response) {
					$scope.checking = false;
					readAttemptsRemaining(response);

					var reason = readReason(response);

					// A link that has stopped working is not something retyping can fix, so it ends the
					// page with the reason rather than inviting another go at an answer that may be right.
					if (LINK_PROBLEMS[reason]) {
						$scope.linkProblem = LINK_PROBLEMS[reason];
						$scope.error = true;
						return;
					}

					// Out of attempts is the end of the road for this link, and pressing on would only
					// produce the same refusal with no way for the patient to act on it. Also reached
					// when a wrong answer has just spent the last attempt.
					if (reason === "ATTEMPT_LIMIT_REACHED" || $scope.attemptsRemaining === 0) {
						$scope.exhausted = true;
						return;
					}

					// Only a wrong answer is reported as one. An older API sends no reason at all, and
					// every refusal it made was a 400, so a reasonless 400 keeps meaning a wrong answer.
					var wrongAnswer = reason === "DOB_MISMATCH"
						|| reason === "MOBILE_CODE_MISMATCH"
						|| (!reason && response && response.status === 400);

					if (!wrongAnswer) {
						$scope.checkFailed = true;
						return;
					}

					// Wipe the bad answer and hand the cursor back, so retrying is one action not five.
					if (step === "code") {
						$scope.codeDigits = blankCode();
						$scope.codeError = true;
						focusBox(0);
					} else {
						$scope.dob = { day: "", month: "", year: "" };
						$scope.dobError = true;
						focusDobField("day");
					}
				}
			);
		}

		// The verify-step reasons that mean the link itself has stopped working, mapped to the panel
		// that explains each. See verify-step-response.json in the API for the full list.
		var LINK_PROBLEMS = {
			EXPIRED: "expired",
			CANCELLED: "cancelled",
			ALREADY_USED: "used",
			TOKEN_NOT_FOUND: "invalid",
			MOBILE_CODE_NOT_REQUIRED: "invalid",
			SERVICE_DISABLED: "invalid",
		};

		function readReason(response) {
			var data = (response && response.data) || {};
			return typeof data.reason === "string" ? data.reason : null;
		}

		// Left alone when the server did not send a count, rather than reset: the last figure we were
		// given is still the best thing we know, and blanking it mid-flow would look like the limit had
		// gone away.
		function readAttemptsRemaining(response) {
			var data = (response && response.data) || {};
			if (typeof data.attempts_remaining === "number") {
				$scope.attemptsRemaining = data.attempts_remaining;
			}
		}

		// The final call carries the consents only. Both credentials were proven at their own steps, so
		// there is nothing left here that the patient could have got wrong.
		function submit() {
			$scope.PatientFormsAPI.isProcessing = true;

			$scope.PatientFormsAPI.submitContactVerification(
				$stateParams.token, $scope.consents.research, $scope.consents.ml
			).then(function () {
				$scope.confirmed = true;
			}, function () {
				// Nothing here is a wrong answer - the answers are already settled. A refusal at this
				// point means the token died between the last step and this call.
				$scope.error = true;
			}).then(function () {
				$scope.PatientFormsAPI.isProcessing = false;
			});
		}

		// ---------------------------------------------------------------------------------------------
		// Resending the texted code.
		// ---------------------------------------------------------------------------------------------

		function startResendCooldown() {
			$scope.resendCooldown = RESEND_COOLDOWN_SECONDS;

			// Cancelled on $destroy below. $interval keeps firing after the view is gone otherwise,
			// which on this page means a timer running against a scope the patient has navigated off.
			cooldownTimer = $interval(function () {
				$scope.resendCooldown--;
			}, 1000, RESEND_COOLDOWN_SECONDS);
		}

		// Shown only on the code step, and only when the server said a resend is possible. A patient
		// with no mobile number never gets the control, and neither does one whose token carries no
		// code because the link itself proved possession.
		$scope.isResendVisible = function () {
			return $scope.isStep("code") && $scope.resendAvailable;
		};

		$scope.isResendAvailable = function () {
			return $scope.isResendVisible() && $scope.resendState !== "sending" && $scope.resendCooldown <= 0;
		};

		$scope.resendCode = function () {
			if (!$scope.isResendAvailable()) return;

			$scope.resendState = "sending";

			PatientFormsAPI.resendMobileCode($stateParams.token).then(
				function () {
					$scope.resendState = "sent";

					// The code the patient is waiting for is the one being resent, so anything already
					// typed stays valid and is left alone. Only a stale "that code was wrong" warning is
					// cleared, since it refers to an attempt that predates the new message.
					$scope.codeError = false;
					startResendCooldown();
				},
				function () {
					// Every refusal comes back as the same 400, so this cannot say which it was. The
					// likely ones - the token has died, or the resends are used up - are not things
					// pressing again will fix, so the control is withdrawn rather than left to fail.
					$scope.resendState = "failed";
					$scope.resendAvailable = false;
				}
			);
		};

		$scope.$on("$destroy", function () {
			cancelAutoAdvance();
			if (cooldownTimer) {
				$interval.cancel(cooldownTimer);
				cooldownTimer = null;
			}
		});
	},
]);
